import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Flex } from 'rebass';
import { useHistory, useLocation } from 'react-router-dom';
import useMode from '../../../../hooks/useMode';
import useNotify from '../../../../hooks/useNotify';
import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import ThirdPane from '../../../../components/ThirdPane';
import ThirdPaneHeader from '../../../../components/ThirdPane/ThirdPaneHeader';
import ThirdPaneContent from '../../../../components/ThirdPane/ThirdPaneContent';
import ThirdPaneFooter from '../../../../components/ThirdPane/ThirdPaneFooter';
import Button from '../../../../components/Button/Button';
import SurveyTemplateForm from '../../../../components/WaterHazard/SurveyTemplateForm';
import HazardSurveyResult from '../../../../components/WaterHazard/HazardSurveyResult';
import {
  CREATE_HAZARD_SURVEY,
  GET_HAZARD_SURVEY_JOB,
  UPDATE_HAZARD_SURVEY,
} from '../../../../components/WaterHazard/queries';
import { GET_WATER_HAZARDS } from '../../../admin/pages/waterHazards/WaterHazards.query';
import { ApolloError } from '@apollo/client/errors';
import { HazardSurveyInput } from '../../../../components/WaterHazard/types';
import HazardSurveyEditForm from '../../../../components/WaterHazard/HazardSurveyEditForm';
import { Label } from '../../../../components/Form';
import { GET_WATER_SERVICE } from '../../../../components/WaterService/WaterServiceThirdPane';
import HazardSurveyTemplatePicker from '../../../../components/WaterHazard/HazardSurveyTemplatePicker';
import WaterServicePicker from '../../../../components/WaterService/WaterServicePicker';

type HazardSurveyEditorProps = RouteComponentProps<{ id: string }>;

function HazardSurveyEditor({ match }: HazardSurveyEditorProps): JSX.Element {
  const jobId = match.params.id === 'new' ? undefined : match.params.id;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [waterServiceId, setWaterServiceId] = useState(
    searchParams.get('waterServiceId')
  );

  const { showError, showSuccess } = useNotify();
  const mode = useMode();
  const history = useHistory();

  const [survey, setSurvey] = useState<HazardSurveyInput>({
    instructions: '',
    otherHazardsQuestion: false,
    allProtectedQuestion: false,
    hazardIds: [],
  });

  const { data: existingData, loading: itemLoading } = useQuery(
    GET_HAZARD_SURVEY_JOB,
    {
      skip: !jobId,
      variables: {
        where: { id: jobId },
      },
    }
  );

  const [selectedWaterService, setSelectedWaterService] = useState<any>(null);

  const { data: waterServiceData, loading: serviceLoading } = useQuery<{
    waterServices: Array<{
      id: string;
      siteNumber: string | null;
      contactProfileId?: string | null;
      assignedTo?: { id: string } | null;
      address: { id: string };
    }>;
  }>(GET_WATER_SERVICE, {
    skip: !!jobId || !waterServiceId,
    variables: {
      where: { id: waterServiceId },
    },
  });

  useEffect(() => {
    if (existingData?.jobs?.[0]?.waterHazardSurvey) {
      const existing = existingData.jobs[0].waterHazardSurvey;
      const job = existingData.jobs[0];
      setSurvey({
        instructions: existing.instructions || '',
        otherHazardsQuestion: !!existing.otherHazardsQuestion,
        allProtectedQuestion: !!existing.allProtectedQuestion,
        hazardIds: existing.items.map((i: any) => i.waterHazardId),
        scheduled: job.scheduled
          ? new Date(job.scheduled).toISOString()
          : undefined,
        contactProfileId: existing.contactProfileId || undefined,
        outboundAssignment: job.outboundAssignment
          ? {
              vendorProfileId: job.outboundAssignment.vendorProfile.id,
            }
          : undefined,
      });
    } else if (waterServiceData?.waterServices?.[0] && !jobId) {
      const service = waterServiceData.waterServices[0];
      setSurvey((prev) => ({
        ...prev,
        contactProfileId: service.contactProfileId || undefined,
        outboundAssignment: service.assignedTo
          ? {
              vendorProfileId: service.assignedTo.id,
            }
          : undefined,
      }));
    }
  }, [existingData, waterServiceData, jobId]);

  const { data: hazardsData } = useQuery(GET_WATER_HAZARDS);

  const [saveSurvey, { loading: saveLoading }] = useMutation<any, any>(
    UPDATE_HAZARD_SURVEY,
    {
      onCompleted: () => {
        showSuccess('Hazard survey saved');
        history.push(`/${mode}/hazard-surveys/${jobId}`);
      },
      onError: (error: ApolloError) => {
        showError('Error saving hazard survey', error);
      },
    }
  );

  const [createSurvey, { loading: createLoading }] = useMutation<any, any>(
    CREATE_HAZARD_SURVEY,
    {
      onCompleted: (data) => {
        showSuccess('Hazard survey created');
        const newJobId = data.createWaterHazardSurvey?.jobId;
        history.push(`/${mode}/hazard-surveys/${newJobId}`);
      },
      onError: (error: ApolloError) => {
        showError('Error creating hazard survey', error);
      },
    }
  );

  const handleSave = useCallback(async () => {
    const { hazardIds, name, description, scheduled, ...rest } = survey;
    const input = {
      ...rest,
      scheduled: scheduled ? new Date(scheduled).getTime() : undefined,
      items: hazardIds?.map((id, index) => ({
        waterHazardId: id,
        displayOrder: index,
      })),
    };

    if (jobId) {
      await saveSurvey({
        variables: {
          jobId,
          input,
        },
      });
    } else {
      await createSurvey({
        variables: {
          waterServiceId,
          input,
        },
      });
    }
  }, [survey, jobId, waterServiceId, saveSurvey, createSurvey]);

  const handleCancel = () => {
    if (jobId) {
      history.push(`/${mode}/hazard-surveys/${jobId}`);
    } else if (waterServiceId) {
      history.push(`/${mode}/water-services/${waterServiceId}`);
    } else {
      history.push(`/${mode}/hazard-surveys`);
    }
  };

  const job = existingData?.jobs?.[0];
  const waterService =
    job?.waterHazardSurvey?.waterService ||
    waterServiceData?.waterServices?.[0] ||
    selectedWaterService;

  const address = job?.address;

  return (
    <ThirdPaneLayout paneVisible={true} paneWidth={640}>
      <MainColumn width="calc(100% - 640px)">
        <Flex flexDirection="column" height="100%">
          <Box p={3} bg="gray.100" flexGrow={1} sx={{ overflowY: 'auto' }}>
            <HazardSurveyResult
              template={{
                instructions: survey.instructions || '',
                otherHazardsQuestion: !!survey.otherHazardsQuestion,
                allProtectedQuestion: !!survey.allProtectedQuestion,
                hazardIds: survey.hazardIds || [],
              }}
              hazards={hazardsData?.waterHazards ?? []}
              waterService={waterService as any}
            />
          </Box>
        </Flex>
      </MainColumn>
      <ThirdPane visible={true}>
        <ThirdPaneHeader
          title={jobId ? 'Edit Hazard Survey' : 'Create Hazard Survey'}
        />
        <ThirdPaneContent p={0}>
          {itemLoading || serviceLoading ? (
            <Flex
              justifyContent="center"
              alignItems="center"
              flexGrow={1}
              p={3}
            >
              Loading...
            </Flex>
          ) : (
            <>
              {!jobId && (
                <Box
                  sx={{ borderBottom: '1px solid', borderColor: 'tableBorder' }}
                >
                  {!searchParams.get('waterServiceId') && (
                    <Box mb={3} px={4} pt={3}>
                      <Label>Water Service</Label>
                      <WaterServicePicker
                        onChange={(ws) => {
                          setWaterServiceId(ws.id);
                          setSelectedWaterService(ws);
                        }}
                      />
                    </Box>
                  )}
                  <Box mb={3}>
                    <HazardSurveyEditForm
                      value={survey}
                      onChange={setSurvey}
                      siteAddressId={waterService?.address?.id}
                    />
                  </Box>
                </Box>
              )}
              <Box mt={4} px={4}>
                <Label>Load template</Label>
                <HazardSurveyTemplatePicker
                  onChange={(template) => {
                    if (template) {
                      setSurvey({
                        ...survey,
                        instructions: template.instructions || '',
                        otherHazardsQuestion:
                          template.otherHazardsQuestion ?? undefined,
                        allProtectedQuestion:
                          template.allProtectedQuestion ?? undefined,
                        hazardIds: template.items.map((i) => i.waterHazardId),
                      });
                    }
                  }}
                />
              </Box>
              <SurveyTemplateForm
                hideNameDescription
                template={{
                  instructions: survey.instructions || '',
                  otherHazardsQuestion: !!survey.otherHazardsQuestion,
                  allProtectedQuestion: !!survey.allProtectedQuestion,
                  hazardIds: survey.hazardIds || [],
                }}
                onChange={(t) => setSurvey({ ...survey, ...t })}
              />
            </>
          )}
        </ThirdPaneContent>
        <ThirdPaneFooter justifyContent="space-between">
          <Button
            variant="primary"
            icon="save"
            onClick={handleSave}
            disabled={
              saveLoading || createLoading || (!jobId && !waterServiceId)
            }
          >
            {saveLoading || createLoading
              ? 'Saving...'
              : jobId
                ? 'Save Changes'
                : 'Create Survey'}
          </Button>
          <Button variant="outline" icon="slash-circle" onClick={handleCancel}>
            Cancel
          </Button>
        </ThirdPaneFooter>
      </ThirdPane>
    </ThirdPaneLayout>
  );
}

export default withRouter(HazardSurveyEditor);
